import { useEffect, useRef, useState } from "react";
import Joyride, { CallBackProps, ACTIONS, EVENTS, STATUS, Step } from "react-joyride";
import type { FeatureGuideState } from "./useFeatureGuide";

interface FeatureGuideProps {
	/** Guide state from useFeatureGuide(). */
	guide: FeatureGuideState;
	/** Joyride step definitions. */
	steps: Step[];
	/** Z-index for the overlay (default 10000). */
	zIndex?: number;
	/** Called when the tour finishes or is dismissed. */
	onComplete?: () => void;
}

// PRO-730: `useGuidedTour.next()` already awaits `waitForTarget` for the
// upcoming step's target before advancing Joyride's controlled `stepIndex`,
// but that wait is a best-effort budget (it resolves either way). If a step
// effect (e.g. a Chakra Drawer animation, or a slow-mounting subtree) takes
// longer than that budget, Joyride still ends up measuring a not-yet-mounted
// target and fires TARGET_NOT_FOUND. Killing the tour on the very first such
// event turns a transient "not mounted *yet*" into "the whole tour closes",
// which is indistinguishable from a genuinely broken/missing target. Retry
// for a bounded budget — remounting Joyride so it re-measures the same
// step — before giving up.
const TARGET_RETRY_BUDGET_MS = 4000;
const TARGET_RETRY_INTERVAL_MS = 150;

/** True once `target` resolves to a real, on-screen element. */
const isTargetReady = (target: Step["target"] | undefined): boolean => {
	if (typeof target !== "string") {
		// HTMLElement refs / undefined — nothing for us to poll for.
		return true;
	}
	return document.querySelector(target) !== null;
};

/**
 * Reusable on-demand feature guide powered by react-joyride.
 *
 * Unlike the legacy GuidedTour (cookie-gated, global), this is designed
 * for contextual "Show Me" tours triggered by the user from within a feature.
 *
 * Usage:
 * ```tsx
 * const guide = useFeatureGuide();
 * <button onClick={guide.start}>Show Me</button>
 * <FeatureGuide guide={guide} steps={panelMeetingSteps} />
 * ```
 */
export const FeatureGuide: React.FC<FeatureGuideProps> = ({
	guide,
	steps,
	onComplete,
	zIndex = 10000,
}) => {
	// Forces Joyride to fully remount (and so re-measure `step.target`) once a
	// bounded retry sees the target has appeared, without changing `stepIndex`.
	const [remountKey, setRemountKey] = useState(0);
	// Tracks the in-flight retry: which step it's for and when to give up.
	const retryRef = useRef<{ stepIndex: number; deadline: number } | null>(null);
	const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearRetry = () => {
		if (retryTimerRef.current !== null) {
			clearTimeout(retryTimerRef.current);
			retryTimerRef.current = null;
		}
		retryRef.current = null;
	};

	// Never leave a pending retry poll running past unmount.
	useEffect(() => clearRetry, []);

	const stopTour = () => {
		clearRetry();
		guide.stop();
		onComplete?.();
	};

	const scheduleRetry = (stepIndex: number, target: Step["target"] | undefined) => {
		if (retryRef.current === null || retryRef.current.stepIndex !== stepIndex) {
			retryRef.current = { stepIndex, deadline: Date.now() + TARGET_RETRY_BUDGET_MS };
		}
		const { deadline } = retryRef.current;

		retryTimerRef.current = setTimeout(() => {
			if (isTargetReady(target)) {
				clearRetry();
				setRemountKey((key) => key + 1);
				return;
			}
			if (Date.now() >= deadline) {
				stopTour();
				return;
			}
			scheduleRetry(stepIndex, target);
		}, TARGET_RETRY_INTERVAL_MS);
	};

	const handleCallback = (data: CallBackProps) => {
		const { status, type, action, index } = data;

		if (action === ACTIONS.CLOSE) {
			stopTour();
			return;
		}

		if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
			stopTour();
			return;
		}

		if (type === EVENTS.STEP_AFTER) {
			clearRetry();
			if (action === ACTIONS.NEXT) {
				guide.next();
			} else if (action === ACTIONS.PREV) {
				guide.prev();
			}
			return;
		}

		// A step's target can legitimately not be mounted *yet* (e.g. a Chakra
		// Drawer animation, or an async effect triggered by the previous step's
		// `onAfterStep`) even though `useGuidedTour` already waited for it —
		// that wait is a best-effort budget, not a guarantee. Retry for a
		// bounded window (re-measuring via a Joyride remount) before treating
		// the target as genuinely absent — only a target that's still missing
		// once that budget is exhausted warrants tearing the whole tour down
		// (it would otherwise get stuck mid-tour with spotlightClicks={false},
		// blocking all page clicks indefinitely).
		if (type === EVENTS.TARGET_NOT_FOUND) {
			scheduleRetry(index, data.step.target);
		}
	};

	return (
		<Joyride
			key={remountKey}
			steps={steps}
			run={guide.isRunning}
			stepIndex={guide.stepIndex}
			continuous
			showProgress
			showSkipButton
			callback={handleCallback}
			scrollToFirstStep
			scrollOffset={100}
			disableOverlayClose
			spotlightClicks={false}
			spotlightPadding={6}
			styles={{
				options: {
					arrowColor: "#fff",
					backgroundColor: "#fff",
					overlayColor: "rgba(0, 0, 0, 0.4)",
					primaryColor: "#4F46E5",
					spotlightShadow: "0 0 0 4px rgba(79, 70, 229, 0.6)",
					textColor: "#333",
					width: 380,
					zIndex,
				},
				spotlight: {
					borderRadius: "6px",
				},
			}}
			locale={{
				back: "Back",
				close: "Got it",
				last: "Got it",
				next: "Next",
				skip: "Skip tour",
			}}
			floaterProps={{
				disableAnimation: false,
			}}
		/>
	);
};
